<!-- eslint-disable vuejs-accessibility/interactive-supports-focus -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<template>
  <div
    class="custom-select"
    :class="{ 'is-invalid': dirty && !!errorMessage }"
    :data-invalid="dirty && !!errorMessage"
  >
    <span :id="`${id}-label`" class="select-label">{{ label }}</span>
    <div v-click-outside="clickOutsite">
      <button
        :id="`${id}-button`"
        class="select-btn"
        type="button"
        aria-haspopup="true"
        :aria-expanded="isDropdownVisible"
        :aria-controls="`${id}-listbox`"
        :aria-labelledby="`${id}-label`"
        :disabled="disabled"
        :aria-disabled="disabled"
        ref="buttonRef"
        @click="toggleDropdown"
        @keydown="pressKeyOnButton"
      >
        {{ selectedOption || modelValue || placeholder }}
      </button>
      <div
        v-show="isDropdownVisible && !disabled"
        class="dd-wrapper"
      >
        <input
          v-model="searchKey"
          class="select-input"
          type="text"
          aria-autocomplete="list"
          :aria-owns="`${id}-listbox`"
          @keydown="pressKeyOnInput"
        />
        <ul
          :id="`${id}-listbox`"
          class="dd-menu"
          role="listbox"
          :aria-labelledby="`${id}-label`"
          tabindex="-1"
          :aria-activedescendant="
            !isDropdownVisible ? '' : `${id}-option-${activeDescendantIndex}`
          "
        >
          <li
            v-for="(option, index) in filteredOptions"
            :key="`${id}-option-${index}`"
            :id="`${id}-option-${index}`"
            :data-index="index"
            role="option"
            ref="optionsRef"
            :data-option="option"
            :aria-selected="index === activeDescendantIndex"
            @click="selectOption"
          >
            {{ option }}
          </li>
        </ul>
        <p
          v-if="!filteredOptions.length"
          class="no-option"
        >
          No available option for this search
        </p>
      </div>
    </div>
    <small v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script setup>
import {
  computed, ref, watch,
} from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  id: { String, required: true },
  label: { String, required: true },
  placeholder: { String, required: true },
  options: { Array, required: true },
  disabled: { Boolean, default: false },
  required: { Boolean, default: false },
  dirty: { Boolean, default: false },
  errorMessage: { String, default: '' },
});

const emit = defineEmits(['selectOption']);

const isDropdownVisible = ref(false);
const selectedOption = ref('');
const searchKey = ref('');
const buttonRef = ref(null);
const optionsRef = ref([]);
const activeDescendantIndex = ref(-1);

const filteredOptions = computed(() => (
  searchKey.value.length > 0
    ? props.options.filter((option) => option.toLowerCase().includes(searchKey.value.toLowerCase()))
    : props.options
));

const clickOutsite = () => {
  isDropdownVisible.value = false;
};

const selectOption = (event) => {
  const { option } = event.target.dataset;

  selectedOption.value = option;
};

const findSelectedOptionIndex = () => {
  let index = activeDescendantIndex.value;

  const targetOption = optionsRef.value.find(
    (option) => option?.dataset.option === selectedOption.value,
  );

  index = targetOption?.dataset.index;

  return index;
};

const scrollToSelectedOption = () => {
  optionsRef.value[activeDescendantIndex.value]?.scrollIntoView({
    behavior: 'smooth', block: 'nearest',
  });
};

const setActiveDescendantIndex = (action) => {
  switch (action) {
    case 'home':
      activeDescendantIndex.value = 0;
      break;
    case 'end':
      activeDescendantIndex.value = optionsRef.value.length - 1;
      break;
    case 'increment':
      activeDescendantIndex.value += 1;
      break;
    case 'decrement':
      activeDescendantIndex.value -= 1;
      break;
    default:
      activeDescendantIndex.value = findSelectedOptionIndex();
      break;
  }

  scrollToSelectedOption();
};

const toggleDropdown = () => {
  if (!isDropdownVisible.value) {
    isDropdownVisible.value = true;
    setActiveDescendantIndex('home');
  } else {
    isDropdownVisible.value = false;
  }
};

const pressArrowDown = (event) => {
  event.preventDefault();

  if (!isDropdownVisible.value) {
    isDropdownVisible.value = true;
  }

  if (activeDescendantIndex.value < optionsRef.value.length - 1) {
    setActiveDescendantIndex('increment');
  } else {
    setActiveDescendantIndex('home');
  }
};

const pressArrowUp = (event) => {
  event.preventDefault();

  if (!isDropdownVisible.value) {
    isDropdownVisible.value = true;
  }

  if (activeDescendantIndex.value > 0) {
    setActiveDescendantIndex('decrement');
  } else {
    setActiveDescendantIndex('end');
  }
};

const pressEnter = (event) => {
  if (isDropdownVisible.value && activeDescendantIndex.value >= 0) {
    event.preventDefault();
    selectedOption.value = optionsRef.value[activeDescendantIndex.value]?.dataset.option;
  }
};

const pressKeyOnButton = (event) => {
  /**
   * If the DOM focus is on the button and down arrow is pressed,
   * the visual focus moves to the next option. If visual focus is
   * on the last option, moves visual focus to the first option.
   */
  if (event.key === 'ArrowDown') pressArrowDown(event);

  /**
   * If the DOM focus is on the button and up arrow is pressed,
   * the visual focus moves to the previous option. If visual focus is
   * on the first option, moves visual focus to the last option.
   */
  if (event.key === 'ArrowUp') pressArrowUp(event);

  /**
   * If the DOM focus is on the button and Enter is pressed while the
   * visual focus is on a listbox option, that option is selected and the
   * listbox is closed. Closing the listbox happens via watching a change
   * on the selectedOption
   */
  if (event.key === 'Enter') pressEnter(event);

  /**
   * If the DOM focus is on the button and If Escape is pressed
   * while a listbox is displayed, the listbox is closed.
   */
  if (event.key === 'Escape') isDropdownVisible.value = false;

  /**
   * Pressing home while the visual focus is on a listbox option,
   * the visual focus is moved to the first option.
   */
  if (event.key === 'Home') {
    if (isDropdownVisible.value) {
      event.preventDefault();
      setActiveDescendantIndex('home');
    }
  }

  /**
   * Pressing end while the visual focus is on a listbox option,
   * the visual focus is moved to the last option.
   */
  if (event.key === 'End') {
    if (isDropdownVisible.value) {
      event.preventDefault();
      setActiveDescendantIndex('end');
    }
  }
};

const pressKeyOnInput = (event) => {
  /**
   * If the DOM focus is on the searchbox and down arrow is pressed,
   * the visual focus moves to the next option. If visual focus is
   * on the last option, moves visual focus to the first option.
   */
  if (event.key === 'ArrowDown') pressArrowDown(event);

  /**
   * If the DOM focus is on the searchbox and up arrow is pressed,
   * the visual focus moves to the previous option. If visual focus is
   * on the first option, moves visual focus to the last option.
   */
  if (event.key === 'ArrowUp') pressArrowUp(event);

  /**
   * If the DOM focus is on the searchbox and Enter is pressed while the
   * visual focus is on a listbox option, that option is selected and the
   * listbox is closed. Closing the listbox happens via watching a change
   * on the selectedOption
   */
  if (event.key === 'Enter') pressEnter(event);

  /**
   * If the DOM focus is on the searchbox and there is text in the searchbox,
   * pressing Escape clears the textbox.
   */
  if (event.key === 'Escape') {
    setActiveDescendantIndex('home');
    searchKey.value = '';
  }
};

watch(
  () => selectedOption.value,
  (val) => {
    isDropdownVisible.value = false;
    buttonRef.value.focus();
    emit('selectOption', val);
  },
);

watch(
  () => isDropdownVisible.value,
  (visible) => {
    if (!visible) activeDescendantIndex.value = -1;
  },
);
</script>

<style lang="scss" scoped>
.custom-select {
  position: relative;

  .select-label {
    font-size: 18px;

    @media (min-width: 1281px) {
      font-size: 20px;
    }
  }

  .select-field-wrapper {
    position: relative;
  }

  .select-btn {
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    border: 2px solid #cdcdcd;
    border-radius: 5px;
    background-color: #fafafa;
    font-size: clamp(18px, 24px, 1.5vw);
    cursor: pointer;
    background-image: url('@/assets/img/chevron-down.svg');
    background-size: 32px;
    background-repeat: no-repeat;
    background-position-x: 98.5%;
    background-position-y: 50%;

    &:hover,
    &:focus {
      border-color: $AC_Orange;
    }

    &:focus {
      outline: 2px solid;
      outline-offset: 1px;
    }
  }

  &.is-invalid .select-btn {
    border-color: $AC_Primary;
  }

  .dd-wrapper {
    padding: 12px 0;
    border: 2px solid #cdcdcd;
    border-radius: 5px;
    background-color: $AC_White;
  }

  .select-input {
    padding: 0 16px;
    box-sizing: border-box;
    font-size: 18px;
    width: calc(100% - 40px);
    height: 40px;
    border-radius: 5px;
    border: 2px solid #cdcdcd;
    margin: 0 12px 15px;
    background-image: url('@/assets/img/search-icon.svg');
    background-size: 18px;
    background-repeat: no-repeat;
    background-position-x: 98.5%;
    background-position-y: 50%;

    &:hover,
    &:focus {
      border-color: $AC_Orange;
    }

    &:focus {
      outline-offset: 3px;
    }
  }

  .no-option {
    padding: 14px;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
  }

  .dd-menu {
    max-height: 300px;
    overflow-y: auto;

    li {
      padding: 5px 12px;
      font-size: 16px;
      cursor: pointer;

      &[aria-selected="true"],
      &:hover,
      &:focus {
        background-color: #2369cc;
        color: $AC_White;
      }
    }
  }

  .error-message {
    font-size: clamp(14px, 18px, 1.25vw);
    color: $AC_Primary;
  }
}

.acura {
  .select-input {
    font-family: 'Avenir';
  }

  .select-btn,
  .select-label {
    font-family: 'Avenir Book';
  }
}

.honda {
  .select-btn,
  .select-input,
  .select-label {
    font-family: 'Myriad Pro';
  }
}
</style>
